import { FileWarning } from 'lucide-react'
import { forwardRef } from 'react'
import { cn } from '@/shared/lib/utils'
import { CapText } from './cap-text'

/**
 * El aviso de «esto no se pudo mostrar», dentro del marco del visor.
 * Réplica de `apps/mobile/src/features/documentos/ui/document-notice.tsx`.
 *
 * ## Por qué es un componente y no tres copias
 *
 * Estaba escrito TRES veces: una en `cap-pdf-viewer.tsx`, otra en `documento-screen.tsx` y la
 * tercera hacía falta para el visor de imagen. Del lado de la app pasó lo mismo —vivía adentro de
 * `pdf-viewer.tsx` hasta que el visor de imagen necesitó decir exactamente lo mismo— y salió a su
 * propio archivo por la misma razón: el motor de PDF y una foto que no baja fallan por motivos
 * distintos, pero la persona ve el mismo hueco y necesita el mismo par —qué pasó y qué se puede
 * hacer— en el mismo lugar de la pantalla.
 *
 * No es un `CapEmptyState`: aquél ocupa la pantalla entera y trae acción. Éste ocupa el recuadro
 * donde iba el documento, con el resto del visor —cabecera y acciones— todavía en pie.
 */
export type CapDocumentNoticeProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> & {
  title: string
  detail: string
}

export const CapDocumentNotice = forwardRef<HTMLDivElement, CapDocumentNoticeProps>(
  function CapDocumentNotice({ title, detail, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'cap-bg-neutral-0 cap-border-neutral-200 flex h-full w-full flex-col items-center justify-center gap-2 rounded-[var(--cap-radius-card)] border px-8',
          className
        )}
        {...props}
      >
        <FileWarning size={40} className="cap-fg-neutral-300" />
        <CapText variant="body" weight="semiBold" className="text-center">
          {title}
        </CapText>
        <CapText variant="caption" tone="muted" className="text-center">
          {detail}
        </CapText>
      </div>
    )
  }
)
